import React, { useState, useEffect } from 'react';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import './ManagementPages.css';

const Leaves = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const [formData, setFormData] = useState({
    employee: '',
    leaveType: 'annual',
    startDate: '',
    endDate: '',
    reason: ''
  });

  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchEmployees();
    fetchLeaves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/api/employees', {
        params: { status: 'active' }
      });
      setEmployees(response.data);
    } catch (err) {
      setError('Failed to load employees');
    }
  };

  const fetchLeaves = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (selectedEmployee) params.employee = selectedEmployee;

      const response = await api.get('/api/leaves', { params });
      setLeaves(response.data);
    } catch (err) {
      setError('Failed to load leaves');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async (employeeId) => {
    if (!employeeId) {
      setBalance(null);
      return;
    }
    try {
      const response = await api.get(`/api/leaves/balance/${employeeId}`);
      setBalance(response.data);
    } catch (err) {
      console.error('Failed to fetch balance:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/api/leaves', formData);
      fetchLeaves();
      if (formData.employee) {
        fetchBalance(formData.employee);
      }
      closeModal();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit leave request');
    }
  };

  const handleApprove = async (id) => {
    try {
      await api.put(`/api/leaves/${id}/approve`);
      fetchLeaves();
      if (selectedEmployee) {
        fetchBalance(selectedEmployee);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve leave');
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Please provide a reason for rejection (optional):');
    try {
      await api.put(`/api/leaves/${id}/reject`, { rejectionReason: reason });
      fetchLeaves();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject leave');
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Are you sure you want to cancel this leave request?')) {
      try {
        await api.delete(`/api/leaves/${id}`);
        fetchLeaves();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to cancel leave');
      }
    }
  };

  const openModal = () => {
    setFormData({
      employee: '',
      leaveType: 'annual',
      startDate: '',
      endDate: '',
      reason: ''
    });
    setBalance(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'employee' && value) {
      fetchBalance(value);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'status-active';
      case 'rejected': return 'status-inactive';
      case 'pending': return 'status-on-leave';
      default: return '';
    }
  };

  const getLeaveTypeLabel = (type) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Leave Management</h1>
        <button className="btn btn-primary" onClick={openModal}>
          + Request Leave
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="filters">
        <select
          value={selectedEmployee}
          onChange={(e) => {
            setSelectedEmployee(e.target.value);
            if (e.target.value) {
              fetchBalance(e.target.value);
            } else {
              setBalance(null);
            }
          }}
          className="filter-select"
        >
          <option value="">All Employees</option>
          {employees.map(emp => (
            <option key={emp._id} value={emp._id}>{emp.name}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {balance && selectedEmployee && (
        <div className="balance-cards">
          <div className="balance-card">
            <h3>Annual Leave</h3>
            <div className="balance-numbers">
              <span className="remaining">{balance.annual.remaining}</span>
              <span className="total">/ {balance.annual.total} days</span>
            </div>
            <div className="balance-used">Used: {balance.annual.used} days</div>
          </div>
          <div className="balance-card">
            <h3>Sick Leave</h3>
            <div className="balance-numbers">
              <span className="remaining">{balance.sick.remaining}</span>
              <span className="total">/ {balance.sick.total} days</span>
            </div>
            <div className="balance-used">Used: {balance.sick.used} days</div>
          </div>
          <div className="balance-card">
            <h3>Casual Leave</h3>
            <div className="balance-numbers">
              <span className="remaining">{balance.casual.remaining}</span>
              <span className="total">/ {balance.casual.total} days</span>
            </div>
            <div className="balance-used">Used: {balance.casual.used} days</div>
          </div>
        </div>
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Employee</th>
              <th>Type</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Days</th>
              <th>Reason</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leaves.map(leave => (
              <tr key={leave._id}>
                <td>{leave.employee?.name}</td>
                <td>{getLeaveTypeLabel(leave.leaveType)}</td>
                <td>{formatDate(leave.startDate)}</td>
                <td>{formatDate(leave.endDate)}</td>
                <td>{leave.days}</td>
                <td className="reason-cell" title={leave.reason}>
                  {leave.reason.length > 30 ? leave.reason.substring(0, 30) + '...' : leave.reason}
                </td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(leave.status)}`}>
                    {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                  </span>
                </td>
                <td>
                  {leave.status === 'pending' && isAdminOrManager && (
                    <>
                      <button
                        className="btn-icon btn-approve"
                        onClick={() => handleApprove(leave._id)}
                        title="Approve"
                      >
                        ✓
                      </button>
                      <button
                        className="btn-icon btn-reject"
                        onClick={() => handleReject(leave._id)}
                        title="Reject"
                      >
                        ✗
                      </button>
                    </>
                  )}
                  {leave.status === 'pending' && (
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleCancel(leave._id)}
                      title="Cancel"
                    >
                      🗑️
                    </button>
                  )}
                  {leave.status === 'rejected' && leave.rejectionReason && (
                    <span title={leave.rejectionReason} className="rejection-info">
                      ℹ️
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {leaves.length === 0 && (
              <tr>
                <td colSpan="8" className="no-data">No leave requests found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Request Leave</h2>
              <button className="close-btn" onClick={closeModal}>x</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Employee *</label>
                <select
                  name="employee"
                  value={formData.employee}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp._id} value={emp._id}>{emp.name}</option>
                  ))}
                </select>
              </div>

              {balance && (
                <div className="balance-info-modal">
                  <p><strong>Available Balance:</strong></p>
                  <span className="balance-tag">Annual: {balance.annual.remaining}</span>
                  <span className="balance-tag">Sick: {balance.sick.remaining}</span>
                  <span className="balance-tag">Casual: {balance.casual.remaining}</span>
                </div>
              )}

              <div className="form-group">
                <label>Leave Type *</label>
                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleChange}
                  required
                >
                  <option value="annual">Annual Leave</option>
                  <option value="sick">Sick Leave</option>
                  <option value="casual">Casual Leave</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    min={formData.startDate}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  required
                  rows="3"
                  placeholder="Please provide a reason for your leave request"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaves;
